import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Trash2, Upload } from 'lucide-react';
import clsx from 'clsx';

import CommonLoader from '../../../../../components/common-loader/CommonLoader';
import { FONT_FILE_ACCEPT, addCustomFont, removeCustomFont, useCustomFonts } from '../../../../config';
import { useMediaStudio } from '../../../../context/MediaStudioContext';
import AssetsState from '../../../../components/assets-state/AssetsState';
import PanelSection from '../../../../components/panel-section/PanelSection';

import scss from './FontsTab.module.scss';

function FontsTab() {
  const { t } = useTranslation('mediaStudio');
  const { features } = useMediaStudio();
  const {
    fonts: brandFonts,
    isFontsLoading,
    fontFamilies,
    loadingFontFileIds,
  } = useSelector((state) => state.mediaStudioSettings);
  const [error, setError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileRef = useRef(null);
  const customFonts = useCustomFonts();

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    setError(null);
    setIsUploading(true);
    const results = await Promise.allSettled(files.map((file) => addCustomFont(file)));
    setIsUploading(false);
    if (results.some((result) => result.status === 'rejected')) {
      setError(t('fontUploadFailed'));
    }
  };

  return (
    <div className={scss.fonts}>
      {features.brandFonts ? (
        <PanelSection title={t('brandFonts')}>
          <AssetsState isLoading={isFontsLoading} isEmpty={!brandFonts.length} emptyText={t('brandFontsEmpty')}>
            <ul className={scss.fontList}>
              {brandFonts.map((font) => {
                const family = fontFamilies[font.id];
                const isFontLoading = loadingFontFileIds.includes(font.id);

                return (
                  <li
                    key={font.id}
                    className={clsx(scss.fontItem, isFontLoading && scss.fontItemLoading)}
                    aria-busy={isFontLoading}>
                    <span style={family ? { fontFamily: `"${family}", sans-serif` } : undefined}>{font.label}</span>
                    <CommonLoader isLoading={isFontLoading} width={16} />
                  </li>
                );
              })}
            </ul>
          </AssetsState>
        </PanelSection>
      ) : null}

      <PanelSection title={t('uploadedFonts')} help={t('uploadedFontsHint')}>
        <button
          type='button'
          className={scss.uploadButton}
          disabled={isUploading}
          onClick={() => fileRef.current?.click()}>
          <Upload size={16} />
          {t('uploadFont')}
        </button>
        <input ref={fileRef} type='file' accept={FONT_FILE_ACCEPT} multiple hidden onChange={handleUpload} />
        <p className={scss.hint}>{t('uploadFontHint')}</p>
        {error ? (
          <p className={scss.error} role='alert'>
            {error}
          </p>
        ) : null}

        <AssetsState isEmpty={!customFonts.length} emptyText={t('noCustomFonts')}>
          <ul className={scss.fontList}>
            {customFonts.map((font) => (
              <li key={font.id} className={scss.fontItem}>
                <span style={{ fontFamily: font.stack }}>{font.label}</span>
                <button
                  type='button'
                  className={scss.iconButton}
                  title={t('removeFont')}
                  aria-label={t('removeFont')}
                  onClick={() => removeCustomFont(font.id)}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </AssetsState>
      </PanelSection>
    </div>
  );
}

export default FontsTab;
